<template>
  <div>
    <h3 id="customer-image" class="h5 margin-bottom-xs">{{ headerText }}</h3>
    <Carousel
      :list="photos"
      item-class="carousel-image"
      :number-of-items="{ large: photosToShowOnDesktopCount, ...ITEMS_PER_PAGE }"
      :gutter="{ large: 24, medium: 8, small: 8 }"
      :max-items-lazy="totalReviewsPhotosCount"
      :show-skeleton="!photosAlreadyLazyLoaded"
      :skeleton-height="isMobile ? '130px' : '200px'"
      :ssr-size="ssrSize"
      @carousel-next="handleNextClick"
    >
      <template #item="{ data, index }">
        <button :tabindex="index" class="media-button" @click="onSelectPhoto(index, $event)">
          <ReviewThumbnail
            :is-video="data.type === 'video'"
            :src="data.thumbnailUrl ?? data.url"
            :alt="data.caption || `Customer carousel item ${index + 1}.`"
          />
        </button>
      </template>
    </Carousel>
  </div>
</template>

<script setup>
import Carousel from '@atomic-ui/carousel/Carousel';
import { computed, ref } from 'vue';
import useReviewsPhotos from '~/modules/reviews/composables/useReviewsPhotos';
import { useModal } from '~/composables/utils/useModal';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import usePageData from '~/composables/usePageData';
import ReviewThumbnail from '~/modules/reviews/components/Media/ReviewThumbnail.vue';

const ITEMS_PER_PAGE = {
  small: 2.33,
  medium: 4.5,
};

const MODAL_NAME = 'full-review-modal';

const props = defineProps({
  isOnTwoLines: {
    type: Boolean,
    required: true,
  },
});

const { open, initModalEntry } = useModal();
const { isMobile } = useDeviceType();
const { fetchReviewsWithPhotos, photos, reviewsPhotos, totalReviewsPhotosCount, photosAlreadyLazyLoaded, hasVideos } = useReviewsPhotos();

const { ssrSize } = usePageData();

const headerText = computed(() => {
  const count = totalReviewsPhotosCount.value;
  const suffix = count > 1 ? 's' : '';
  if (hasVideos.value) {
    return `${count} customer photos & videos`;
  }
  return `${count} customer image${suffix}`;
});

/** @type {import('vue').Ref<number>} */
const clickedPhotoIndex = ref(0);

const isLoading = ref(false);
const aspectRatio = ref(null);

/** @type {import('vue').ComputedRef<number>} */
const photosToShowOnDesktopCount = computed(() => (props.isOnTwoLines ? 6 : 3));

/**
 * @description updates carousel tracker and fetches remaining review photos that haven't been lazy loaded
 *
 * @param {number} currentPageNum
 * @param  {number} lazyLoadedTotalPagesNum
 */
async function handleNextClick(currentPageNum, lazyLoadedTotalPagesNum) {
  const offset = photos.value.length;
  if (
    !photosAlreadyLazyLoaded.value &&
    !isLoading.value &&
    lazyLoadedTotalPagesNum - currentPageNum <= 2 // prepare 2 more page
  ) {
    // only fetches photos that are outstanding
    isLoading.value = true;
    clickedPhotoIndex.value = currentPageNum;
    await fetchReviewsWithPhotos(offset);
    isLoading.value = false;
  }
}

initModalEntry({
  name: MODAL_NAME,
  props: computed(() => ({
    photos: photos.value,
    modalName: MODAL_NAME,
    isLoading: isLoading.value,
    reviews: reviewsPhotos.value,
    aspectRatio: aspectRatio.value,
    selectedIndex: clickedPhotoIndex.value,
    isCarouselMode: true,
  })),
  evtHandlers: {
    'fetch-photos': handleNextClick,
  },
  componentName: 'reviews',
});

/**
 * opens modal based on selected index clicked
 * @param {number} index
 */
function onSelectPhoto(index, event) {
  const img = event?.target;
  aspectRatio.value = img ? img.naturalWidth / img.naturalHeight : null;
  clickedPhotoIndex.value = index;
  open('full-review-modal');
}
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.media-button {
  width: 100%;
  height: 100%;
}

:deep(.v-carousel-wrapper) {
  li.cell:has(> div.skeleton-class) {
    width: 200px !important;
  }
}
// this was changed to h3 tag, and gets styles from global override
// that's why need to be overriden here. There is TODO for that TD#1
h3#customer-image {
  @include breakpoint(large up) {
    margin-bottom: rem-calc(16) !important;
  }
}
</style>
